import { queryOptions, infiniteQueryOptions } from "@tanstack/react-query";
import {
    ClaimParams,
    ClaimsItemDto,
    getClaims,
    getClaimsInfo,
    getClaimsMember,
    ClaimsByBranchDto,
    getClaimsInfoByBranch,
    getClaimRecipientDetails,
    getSubclaimHandlerEmail,
    getCompanyAssignedSubclaimsOfClaim
} from "../claimsApi";
import { NormalizedSearchResultDto } from "../claimsInvoicingApi";
import { ClaimsDetailsDto, MemberDetailsDto, ClaimsInfoByBranchDto } from "@/types";

const normalizeClaimsId = (claimsId: string): string => claimsId.replaceAll(/\s/g, "");

const getClaimsByCompanyIdQueryOptions = (params: ClaimParams) =>
    queryOptions<NormalizedSearchResultDto<ClaimsItemDto>>({
        queryKey: ["claimsByCompanyId", params.companyId, params.queryFilter],
        queryFn: () => getClaims(params.companyId ?? 0, params.queryFilter)
    });

// Infinite Query Options for paginated claims fetching
export const getClaimsByCompanyIdInfiniteQueryOptions = (params: ClaimParams) =>
    infiniteQueryOptions<NormalizedSearchResultDto<ClaimsItemDto>>({
        queryKey: ["claimsByCompanyIdInfinite", params.companyId, params.queryFilter],
        initialPageParam: params.queryFilter.page,
        queryFn: ({ pageParam }) =>
            getClaims(params.companyId ?? 0, {
                ...params.queryFilter,
                page: pageParam as number
            }),
        enabled: Boolean(params.queryFilter.query),
        getNextPageParam: lastPage => {
            const limit = params.queryFilter.limit;

            // If backend returned fewer than requested → no more pages
            if (!lastPage.items || lastPage.items.length < limit) {
                return undefined;
            }

            // Use the normalized pageIndex returned by backend
            return lastPage.pageIndex + 1;
        }
    });

const getClaimsInfoByIdQueryOptions = (claimsId: string | undefined) => {
    const normalizedClaimsId = claimsId ? normalizeClaimsId(claimsId) : "";
    return queryOptions<ClaimsDetailsDto>({
        queryKey: ["claimsInfo", normalizedClaimsId],
        queryFn: () => getClaimsInfo(normalizedClaimsId),
        enabled: Boolean(claimsId),
        retry: false
    });
};
const getClaimsMemberQueryOptions = (claimsId: string) => {
    const normalizedClaimsId = normalizeClaimsId(claimsId);
    return queryOptions<MemberDetailsDto>({
        queryKey: ["claimsMember", normalizedClaimsId],
        queryFn: () => getClaimsMember(normalizedClaimsId)
    });
};

const getClaimsInfoByBranchQueryOptions = (params: ClaimsByBranchDto) => {
    return queryOptions<ClaimsInfoByBranchDto>({
        queryKey: ["claimsInfoByBranch", params.claimsId, params.crmId],
        queryFn: () => getClaimsInfoByBranch(params.claimsId, params.crmId)
    });
};

const getClaimRecipientDetailsQueryOptions = (claimsId: string | undefined, crmId?: string) => {
    const normalizedClaimsId = claimsId ? normalizeClaimsId(claimsId) : "";
    return queryOptions<MemberDetailsDto>({
        queryKey: ["claimAndRecipientDetails", normalizedClaimsId, crmId],
        queryFn: () => getClaimRecipientDetails(normalizedClaimsId, crmId),
        enabled: Boolean(claimsId)
    });
};

const getSubclaimHandlerEmailQueryOptions = (claimsId: string | undefined, subClaimId: string | undefined) => {
    const normalizedClaimsId = claimsId ? normalizeClaimsId(claimsId) : "";
    return queryOptions<{ email: string }>({
        queryKey: ["subclaimHandlerEmail", normalizedClaimsId, subClaimId],
        queryFn: () => getSubclaimHandlerEmail(normalizedClaimsId, subClaimId ?? ""),
        enabled: Boolean(claimsId) && Boolean(subClaimId)
    });
};

const getCompanyAssignedSubclaimsOfClaimQueryOptions = (claimsId: string | undefined, crmId: string | undefined) => {
    const normalizedClaimsId = claimsId ? normalizeClaimsId(claimsId) : "";
    return queryOptions<string[]>({
        queryKey: ["companyAssignedSubclaimsOfClaim", normalizedClaimsId, crmId],
        queryFn: () => getCompanyAssignedSubclaimsOfClaim(normalizedClaimsId, crmId ?? ""),
        enabled: Boolean(claimsId) && Boolean(crmId)
    });
};
export {
    getClaimsByCompanyIdQueryOptions,
    getClaimsInfoByIdQueryOptions,
    getClaimsMemberQueryOptions,
    getClaimsInfoByBranchQueryOptions,
    getClaimRecipientDetailsQueryOptions,
    getSubclaimHandlerEmailQueryOptions,
    getCompanyAssignedSubclaimsOfClaimQueryOptions
};
